import {
  Box,
  Divider,
  TextField as MuiTextField,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  type ExposureOrGroup,
  type ObjectLiteral,
  type TPaymentOption,
  updateUwQuestions,
  useSubmission,
  useUpdatePaymentPlan,
} from 'api/submission';
import {
  ADDRESS_LINE_MAX_LENGTH,
  DEFAULT_TOGGLE_BUTTONS_GROUP_OPTIONS,
  entityTypes,
  FEIN_FIELD_CODE,
  filloutFormShowConditions,
  isPurchaseActionDisabled,
  PURCHASE_ACTION_DISABLED_TOOLTIP_MESSAGE,
  PURCHASE_BLOCKED_MISSING_DOCUMENTS_MESSAGE,
  SCHEDULED_BUILDING_OCCUPANCY_VALUES,
  SHORT_VIN_INFO_MESSAGE,
  SSN_FIELD_CODE,
  STATES,
  TAX_ID_TYPE_CHOICE_EMPTY_FIELD_CODE,
  TAX_ID_TYPE_CHOICE_FEIN_FIELD_CODE,
  TAX_ID_TYPE_CHOICE_FEIN_FIELD_NAME,
  TAX_ID_TYPE_CHOICE_SSN_FIELD_CODE,
  TAX_ID_TYPE_CHOICE_SSN_FIELD_NAME,
  UNEMPLOYMENT_NUMBER_UW_QUESTION,
  VALIDATION_MESSAGES,
  VIN_LENGTH,
} from 'common/constants';
import { FormCheckbox } from 'components/lib/FormCheckbox';
import { FormControl } from 'components/lib/FormControl';
import { PatternTextField } from 'components/lib/PatternTextField';
import { maskedInputsConfig } from 'components/lib/QuestionEngine';
import { TextField } from 'components/lib/TextField';
import { ToggleButtonsGroup } from 'components/lib/ToggleButtonsGroup';
import { useFormik } from 'formik';
import { formatLocation, isWCIncluded, parseLocation } from 'helpers/utils';
import { isShortVin, sanitizeVinInput, shouldApplyShortVinRules } from 'helpers/vehicleVin';
import { isEmpty, isEqual, isObject, omit } from 'lodash-es';
import theme from 'providers/theme/theme';
import React, {
  type ChangeEvent,
  type FC,
  type MouseEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { NumberFormatValues } from 'react-number-format';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { validateVINFormat } from 'routes/Exposures/Vehicles/components/Modal/VehicleModal.helper';
import { useFullscreenLoader } from 'store/features/UI';
import { useAppSelector } from 'store/hooks';
import * as yup from 'yup';

import { LAUNCH_DARKLY_FLAGS } from 'common/launchDarklyFlags';
import { displayBackendErrorMessage } from 'components/DisplayBackEndErrorMessage';
import { PrimaryButton } from 'components/lib/PrimaryButton';
import { TertiaryButton } from 'components/lib/TertiaryButton';
import { ManualAddressInput } from 'components/ManualAddressInput';
import type { ManualAddressInputRef } from 'components/ManualAddressInput/ManualAddressInput.types';
import { useBulkExposuresOrGroupFields } from 'hooks/useBulkExposuresOrGroupFields';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import FilloutForm from './components/FilloutForm/FilloutForm';
import { PurchaseNavigationButtonMobileStyle } from './Purchase.styles';
import {
  EInfoAiTypes,
  type OperationFieldInputType,
  type PropertiesGroupField,
  type PurchaseFormValues,
  type VehicleGroupField,
} from './Purchase.types';

const HAS_FEIN_MAPPED_VALUES = {
  [TAX_ID_TYPE_CHOICE_EMPTY_FIELD_CODE]: '',
  [TAX_ID_TYPE_CHOICE_FEIN_FIELD_CODE]: 'Yes',
  [TAX_ID_TYPE_CHOICE_SSN_FIELD_CODE]: 'No',
};

const TAX_ID_TYPE_MAPPED_VALUES = {
  fein: TAX_ID_TYPE_CHOICE_FEIN_FIELD_CODE,
  ssn: TAX_ID_TYPE_CHOICE_SSN_FIELD_CODE,
};

const SET_TIMEOUT_DELAY_FOR_NAVIGATION = 2000;

/**
 * Helper function to check if an Additional Interest matches a subject ID.
 * Handles both legacy string format and current array format for info_ai_subject.
 */
const aiMatchesSubject = (ai: ExposureOrGroup, subjectId: string): boolean =>
  Array.isArray(ai.data.info_ai_subject)
    ? ai.data.info_ai_subject.includes(subjectId)
    : ai.data.info_ai_subject === subjectId;

// eslint-disable-next-line complexity
const Purchase: FC = () => {
  const navigate = useNavigate();

  const isMobileLayout = useMediaQuery(theme.breakpoints.down('sm'));

  // State to control the visibility of the FilloutForm component
  const [shouldShowFilloutFormAfterSave, setShouldShowFilloutFormAfterSave] = useState(false);

  const PurchaseNavigationButtonRef = useRef<HTMLDivElement>(null);
  const [purchaseNavigationButtonHeight, setPurchaseNavigationButtonHeight] = useState<
    number | null
  >(null);
  // #region submission
  const { id: submissionId } = useParams() as { id: string };
  const [searchParams] = useSearchParams();

  const {
    data: submission,
    isLoading: isSubmissionLoading,
    refetch: refetchSubmission,
  } = useSubmission(submissionId);
  const submissionStatus = submission?.status;
  const isViewOnly =
    submissionStatus === 'accepted' ||
    submissionStatus === 'signature_pending' ||
    submissionStatus === 'documents_pending';
  const primaryLocation = formatLocation(
    omit(
      submission?.data.exposures.location.at(0)?.data?.loc_address_destructured,
      'latitude',
      'longitude',
    ),
  ).showing;
  const primaryLocationAddressString = primaryLocation.head + primaryLocation.tail;
  // #endregion

  // #region fillout form show conditions
  const isShouldShowFilloutForm = useMemo(
    () => filloutFormShowConditions.some((condition) => condition(submission)),
    [submission],
  );
  // #endregion

  // #region uw_questions and policy data
  const { uw_questions } = submission?.data || {};

  // FEIN and SSN might appear empty due to lack of sync from the agent portal,
  // but if the submission has accepted status (`isViewOnly === true`), those fields were definitely filled.
  // So `!isViewOnly` ensures we only show the section when the data truly hasn't been entered yet.
  const renderOperationsSection =
    isEmpty(uw_questions?.fein) && isEmpty(uw_questions?.ssn) && !isViewOnly;

  const renderUnemploymentNumberQuestion =
    !!submission &&
    isWCIncluded(submission).isWCIncluded &&
    submission?.data.policy_data.pol_state === STATES.MN &&
    submission?.data.uw_questions.entityType !== entityTypes.SOLE_PROPRIETOR.code &&
    submission?.data.uw_questions.numEmployees > 0;
  // #endregion

  // #region exposures and group fields and async actions
  const { building: buildings, vehicle: vehicles } = submission?.data?.exposures || {};
  const scheduledBuildings = buildings?.filter((building) =>
    SCHEDULED_BUILDING_OCCUPANCY_VALUES.includes(building.data.bdg_occupancy),
  );

  const manualAddressInputRefs = useRef<{ [key: string]: ManualAddressInputRef }>({});

  const renderPropertiesSection = !isEmpty(scheduledBuildings);
  const renderVehiclesSection = !isEmpty(vehicles);

  const isFeinRequiredForBusinessStructure = [
    entityTypes.PARTNERSHIP.code,
    entityTypes.LIMITED_PARTNERSHIP.code,
  ].includes(submission?.data.uw_questions.entityType);

  const [isVehicleVinRequiredEnabled] = useFeatureFlags([
    {
      flagKey: LAUNCH_DARKLY_FLAGS['vehicle-vin-required-enabled'],
      defaultValue: false,
    },
  ]);

  const isBusinessRequiredDocumentsMissing = !!submission?.are_business_required_documents_missing;

  const isPurchaseButtonDisabled =
    (isPurchaseActionDisabled && !isViewOnly) || isBusinessRequiredDocumentsMissing;

  const purchaseButtonDisabledMessage = isBusinessRequiredDocumentsMissing
    ? PURCHASE_BLOCKED_MISSING_DOCUMENTS_MESSAGE
    : PURCHASE_ACTION_DISABLED_TOOLTIP_MESSAGE;

  const { info_ai } = submission?.data?.groups || {};

  const { bulkAddGroupField, bulkUpdateGroupField, bulkUpdateExposure, bulkDeleteGroupField } =
    useBulkExposuresOrGroupFields([]);

  // #endregion

  const { mutateAsync: _updatePaymentPlan } = useUpdatePaymentPlan(submissionId);

  const {
    actions: { setSection },
  } = useAppSelector('quoteStepper', ['actions']);

  const { setFullscreenLoading } = useFullscreenLoader();

  // #region validationSchema
  const validationSchema = yup.lazy(() =>
    yup.object().shape({
      ...(renderOperationsSection && {
        hasFein: yup.string().required(),
        fein: yup.string().when('hasFein', {
          is: (hasFein: string) => hasFein === 'Yes',
          then: () =>
            yup
              .string()
              .required()
              .length(9)
              .test('fein', VALIDATION_MESSAGES.dummyTaxIDValue, (val) => new Set(val).size > 1),
          otherwise: () => yup.string().notRequired(),
        }),
        ssn: yup.string().when('hasFein', {
          is: (hasFein: string) => hasFein === 'No',
          then: () =>
            yup
              .string()
              .required()
              .length(9)
              .test('ssn', VALIDATION_MESSAGES.dummyTaxIDValue, (val) => new Set(val).size > 1),
          otherwise: () => yup.string().notRequired(),
        }),
      }),

      ...(renderUnemploymentNumberQuestion && {
        [UNEMPLOYMENT_NUMBER_UW_QUESTION.questionCode]: yup.string().required(),
        [UNEMPLOYMENT_NUMBER_UW_QUESTION.fieldCode]: yup
          .string()
          .when(UNEMPLOYMENT_NUMBER_UW_QUESTION.questionCode, {
            is: (hasUnemploymentNumber: string) => hasUnemploymentNumber === 'Yes',
            then: () => yup.string().required(),
            otherwise: () => yup.string().notRequired(),
          }),
      }),

      ...(renderPropertiesSection &&
        buildings?.reduce((acc, building) => {
          acc[building.id] = yup.object().shape({
            hasLienHolder: yup.boolean().notRequired(),
            info_ai_name: yup.string().when('hasLienHolder', {
              is: (hasLienHolder: boolean) => hasLienHolder === true,
              then: () => yup.string().required(),
              otherwise: () => yup.string().notRequired(),
            }),
            address: yup.string().when('hasLienHolder', {
              is: (hasLienHolder: boolean) => hasLienHolder === true,
              then: () => yup.string().required(),
              otherwise: () => yup.string().notRequired(),
            }),
          });
          return acc;
        }, {})),

      ...(renderVehiclesSection &&
        vehicles?.reduce((acc, vehicle) => {
          acc[vehicle.id] = yup.object().shape({
            ...(isEmpty(vehicle.data.veh_vin) && {
              veh_vin: shouldApplyShortVinRules(isVehicleVinRequiredEnabled, vehicle.data)
                ? yup.string().required()
                : yup
                    .string()
                    .required()
                    .test(
                      'vin-format',
                      VALIDATION_MESSAGES.invalidVINFormat,
                      (val) => !val || validateVINFormat(val),
                    ),
            }),
            isLeasedOrFinanced: yup.boolean().notRequired(),
            info_ai_name: yup.string().when('isLeasedOrFinanced', {
              is: (isLeasedOrFinanced: boolean) => isLeasedOrFinanced === true,
              then: () => yup.string().required(),
              otherwise: () => yup.string().notRequired(),
            }),
            info_ai_address_phone: yup.string().when('isLeasedOrFinanced', {
              is: (isLeasedOrFinanced: boolean) => isLeasedOrFinanced === true,
              then: () => yup.string().required(),
              otherwise: () => yup.string().notRequired(),
            }),
          });
          return acc;
        }, {})),
    }),
  );
  // #endregion

  // #region formik
  const formik = useFormik<PurchaseFormValues>({
    initialValues: {
      ...(renderOperationsSection && {
        hasFein: isFeinRequiredForBusinessStructure
          ? HAS_FEIN_MAPPED_VALUES[TAX_ID_TYPE_CHOICE_FEIN_FIELD_CODE]
          : HAS_FEIN_MAPPED_VALUES[uw_questions?.taxIDType],
        fein: '',
        ssn: '',
      }),

      ...(renderUnemploymentNumberQuestion && {
        [UNEMPLOYMENT_NUMBER_UW_QUESTION.questionCode]: '',
        [UNEMPLOYMENT_NUMBER_UW_QUESTION.fieldCode]: '',
      }),

      ...(renderPropertiesSection &&
        buildings?.reduce((acc, building) => {
          const buildingAIFromSubmission = info_ai?.find((ai) =>
            aiMatchesSubject(ai, building.data.bdg_id),
          );

          if (buildingAIFromSubmission) {
            const {
              info_ai_address_line1,
              info_ai_address_line2,
              info_ai_address_state,
              info_ai_address_zip,
              info_ai_address_city,
              info_ai_name,
            } = buildingAIFromSubmission.data;

            const addressParts = [
              info_ai_address_line1,
              info_ai_address_line2,
              info_ai_address_city,
              info_ai_address_state,
              info_ai_address_zip,
            ];

            // in case if address is empty not to show commas and other symbols in address input field
            const address = addressParts.every((part) => isEmpty(part))
              ? ''
              : addressParts.join(',');

            acc[building.id] = {
              hasLienHolder: true,
              info_ai_name,
              address,
              is_added: true,
            };
          } else {
            acc[building.id] = {
              hasLienHolder: false,
              info_ai_name: '',
              address: '',
              is_added: false,
            };
          }

          acc[building.id] = {
            ...acc[building.id],
            info_ai_type: EInfoAiTypes.MORTGAGEE,
            info_ai_subject: building.data.bdg_id,
          };

          return acc;
        }, {})),

      ...(renderVehiclesSection &&
        vehicles?.reduce((acc, vehicle) => {
          const vehicleAIFromSubmission = info_ai?.find((ai) =>
            aiMatchesSubject(ai, vehicle.data.veh_id),
          );

          if (vehicleAIFromSubmission) {
            const { info_ai_address_phone, info_ai_name } = vehicleAIFromSubmission.data;

            acc[vehicle.id] = {
              isLeasedOrFinanced: true,
              info_ai_name,
              info_ai_address_phone,
              is_added: true,
            };
          } else {
            acc[vehicle.id] = {
              isLeasedOrFinanced: false,
              info_ai_name: '',
              info_ai_address_phone: '',
              is_added: false,
            };
          }

          acc[vehicle.id] = {
            ...acc[vehicle.id],
            ...(isEmpty(vehicle.data.veh_vin) && {
              veh_vin: '',
            }),
            info_ai_type: EInfoAiTypes.LOSS_PAYEE,
            info_ai_subject: vehicle.data.veh_id,
          };

          return acc;
        }, {})),
    },
    validationSchema,
    onSubmit: () => {},
  });
  // #endregion

  // #region operation input field
  const operationsInputField = useMemo(() => {
    switch (formik.values.hasFein) {
      case 'Yes': {
        return FEIN_FIELD_CODE;
      }
      case 'No': {
        return SSN_FIELD_CODE;
      }
      default:
        return '';
    }
  }, [formik.values.hasFein]);
  /**
   * Renders the input field based on the specified input type.
   *
   * For 'fein', it displays a pattern input field with error handling.
   * For 'ssn', it displays a pattern input field along with additional instructional text
   * and also handles errors.
   *
   * Depending on the input type, it either renders a FEIN input field or an SSN input field.
   */
  const renderOperationInputField = (inputType: OperationFieldInputType) => {
    switch (inputType) {
      case FEIN_FIELD_CODE:
        return (
          <FormControl
            error={!!(formik.touched.fein && formik.errors.fein)}
            errorMessage={formik.errors.fein as string}
          >
            <PatternTextField
              field={{
                code: FEIN_FIELD_CODE,
              }}
              error={!!(formik.touched.fein && formik.errors.fein)}
              onValueChange={handlePatternFormatInputChange}
              placeholder={TAX_ID_TYPE_CHOICE_FEIN_FIELD_NAME}
              valueIsNumericString
              format={maskedInputsConfig.fein.format}
              mask={maskedInputsConfig.fein.mask}
              inputProps={{ inputMode: 'numeric' }}
            />
          </FormControl>
        );

      case SSN_FIELD_CODE:
        return (
          <FormControl
            error={!!(formik.touched.ssn && formik.errors.ssn)}
            errorMessage={formik.errors.ssn as string}
          >
            <Typography variant="h2" mb={1.4}>
              What is your Social Security Number?
            </Typography>

            <Typography variant="body1" mb={1.4}>
              If there is no Federal Tax ID Number, you can provide the business owner’s social
              security number instead.
            </Typography>

            <PatternTextField
              field={{
                code: SSN_FIELD_CODE,
              }}
              required
              onValueChange={handlePatternFormatInputChange}
              placeholder={TAX_ID_TYPE_CHOICE_SSN_FIELD_NAME}
              valueIsNumericString
              format={maskedInputsConfig.ssn.format}
              mask={maskedInputsConfig.ssn.mask}
              inputProps={{
                className: 'fs-mask',
                inputMode: 'numeric',
              }}
            />
          </FormControl>
        );

      default:
        return <></>;
    }
  };
  // #endregion

  // #region field error parser
  /**
   * Parses a field code to determine if there is an error in the form for the given field.
   *
   * If the field code contains a dot (`.`), it treats the code as referring to a nested field.
   * The function checks both touched and error states in the formik object for the corresponding fields.
   *
   * - `error` (boolean): Indicates whether the field has been touched and has an associated error.
   * - `errorMessage` (string | undefined): The error message for the field if it exists.
   */
  const fieldErrorParser = (code: string) => {
    if (code.includes('.')) {
      const [objectName, nestedFieldName] = code.split('.');

      return {
        error:
          formik?.touched[objectName]?.[nestedFieldName] &&
          Boolean(formik.errors[objectName]?.[nestedFieldName]),
        errorMessage: formik?.errors[objectName]?.[nestedFieldName],
      };
    }

    return {
      error: formik?.touched[code] && Boolean(formik?.errors[code]),
      errorMessage: formik?.errors[code],
    };
  };
  // #endregion

  // #region input event handlers
  /**
   * Handles the change event for a ToggleButtonGroup component.
   *
   * If the new value is not null, the function updates the form values, specifically the
   * `hasFein` field with the new value. It also resets the `fein` and `ssn` fields and
   * clears any existing validation errors for these fields.
   */
  const handleOnChangeToggleButtonGroups = (_: MouseEvent<HTMLElement>, newValue: string) => {
    if (newValue === null) return;

    formik.setValues({
      ...formik.values,
      hasFein: newValue,
      fein: '',
      ssn: '',
    });

    formik.setFieldError(FEIN_FIELD_CODE, '');
    formik.setFieldError(SSN_FIELD_CODE, '');
  };

  /**
   * Handles the change event for a hasUnemploymentNumber field ToggleButtonGroup component.
   *
   * If the new value is not null, the function updates the form values, specifically the
   * `hasUnemploymentNumber` field with the new value. It also resets the `UnemploymentNumber` field and
   * clears any existing validation error for these field.
   */
  const handleHasUnemploymentNumberChange = (_: MouseEvent<HTMLElement>, newValue: string) => {
    if (newValue === null) return;

    formik.setValues({
      ...formik.values,
      [UNEMPLOYMENT_NUMBER_UW_QUESTION.questionCode]: newValue,
      [UNEMPLOYMENT_NUMBER_UW_QUESTION.fieldCode]: newValue === 'No' ? '000' : '',
    });

    formik.setFieldError(UNEMPLOYMENT_NUMBER_UW_QUESTION.questionCode, '');
  };

  /**
   * Handles the change event for an input which controls UnemploymentNumber field.
   */
  const handleUnemploymentNumberValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    formik.setFieldValue(UNEMPLOYMENT_NUMBER_UW_QUESTION.fieldCode, newValue);
  };

  /**
   * Handles the change event for a pattern-formatted input field.
   *
   * This function extracts the `value` from the `NumberFormatValues` object and updates the formik field
   * specified by `operationsInputField` with this value.
   */
  const handlePatternFormatInputChange = (values: NumberFormatValues) => {
    const { value = '' } = values;

    formik.setFieldValue(operationsInputField, value);
  };

  /**
   * Handles the change event for a checkbox input.
   *
   * It also clears specified form fields and resets their errors.
   */
  const handleCheckboxOnChange = async (
    e: React.SyntheticEvent<Element, Event>,
    clearFields: string[],
  ) => {
    formik.handleChange(e);

    clearFields.forEach((field) => {
      formik.setFieldError(field, '');
      formik.setFieldValue(field, '');
      formik.setFieldTouched(field, false);
    });
  };
  // #endregion

  // #region validate formik
  const validateFormik = async () => {
    await formik.submitForm();

    const errors = await formik.validateForm();

    if (!isEmpty(errors)) {
      await formik.setTouched(
        {
          ...formik.touched,

          [UNEMPLOYMENT_NUMBER_UW_QUESTION.questionCode]: true,
          [UNEMPLOYMENT_NUMBER_UW_QUESTION.fieldCode]: true,

          hasFein: true,
          fein: true,
          ssn: true,

          ...(renderPropertiesSection &&
            buildings?.reduce((acc, building) => {
              acc[building.id] = {
                hasLienHolder: true,
                info_ai_name: true,
                address: true,
              };
              return acc;
            }, {})),

          ...(renderVehiclesSection &&
            vehicles?.reduce((acc, vehicle) => {
              acc[vehicle.id] = {
                ...(isEmpty(vehicle.data.veh_vin) && {
                  veh_vin: true,
                }),
                isLeasedOrFinanced: true,
                info_ai_name: true,
                info_ai_address_phone: true,
              };
              return acc;
            }, {})),
        },
        false,
      );

      return false;
    }

    return true;
  };
  // #endregion

  // #region Save submission data
  /**
   * Saves data from the Formik form to the backend by constructing an update object
   * based on the current form values and sending appropriate API requests.
   */
  const saveData = async () => {
    const updateObject: ObjectLiteral = {};

    // collects data from formik and inserts into update object
    // eslint-disable-next-line complexity
    Object.entries(formik.values).forEach(([key, value]) => {
      if (!isEmpty(operationsInputField) && key === operationsInputField) {
        updateObject.taxNumber = {
          code: operationsInputField,
          value,
        };
      }

      if (
        key === UNEMPLOYMENT_NUMBER_UW_QUESTION.fieldCode ||
        key === UNEMPLOYMENT_NUMBER_UW_QUESTION.questionCode
      ) {
        updateObject[key] = {
          code: key,
          value,
        };
      }

      if (
        renderPropertiesSection &&
        isObject(value) &&
        value?.info_ai_type === EInfoAiTypes.MORTGAGEE
      ) {
        const { info_ai_type, info_ai_subject, info_ai_name, address, is_added, hasLienHolder } =
          value as PropertiesGroupField;

        const submissionAiProperty = info_ai?.find((ai) =>
          aiMatchesSubject(ai, info_ai_subject as string),
        );

        if (hasLienHolder) {
          // parse address into variables to be suitable for request payload
          const parsedAddress = parseLocation(address);

          // creates formikAiProperty based on data from formik
          const formikAiProperty: Partial<ExposureOrGroup> = {
            name: 'info_ai',
            data: {
              info_ai_type,
              info_ai_name,
              info_ai_subject: [info_ai_subject], // Backend expects array for all info_ai
              info_ai_address_city: parsedAddress.city || '',
              info_ai_address_state: parsedAddress.state || '',
              info_ai_address_zip: parsedAddress.zipcode || '',
              info_ai_address_line1: parsedAddress.street_line || '',
              info_ai_address_line2: parsedAddress?.secondary || '',
            },
          };

          // based on is_added variables added entity to update or add array in updateObject
          if (is_added) {
            delete submissionAiProperty?.perils;
            delete submissionAiProperty?.data.socotra_locator;

            formikAiProperty.id = submissionAiProperty?.id;

            if (!isEqual(submissionAiProperty?.data, formikAiProperty.data)) {
              updateObject.groupFieldsUpdate = [
                ...(updateObject?.groupFieldsUpdate || []),
                formikAiProperty,
              ];
            }
          } else {
            updateObject.groupFieldsAdd = [
              ...(updateObject?.groupFieldsAdd || []),
              formikAiProperty,
            ];
          }
        } else if (!hasLienHolder && submissionAiProperty) {
          updateObject.groupFieldsDelete = [
            ...(updateObject?.groupFieldsDelete || []),
            omit(submissionAiProperty, 'data'),
          ];
        }
      }

      if (
        renderVehiclesSection &&
        isObject(value) &&
        value?.info_ai_type === EInfoAiTypes.LOSS_PAYEE
      ) {
        const {
          veh_vin,
          info_ai_name,
          info_ai_subject,
          info_ai_type,
          info_ai_address_phone,
          isLeasedOrFinanced,
          is_added,
        } = value as VehicleGroupField;

        if (veh_vin) {
          const vehicleExposure = vehicles?.find((v) => v.id === key);

          updateObject.exposuresVehicles = [
            ...(updateObject?.exposuresVehicles || []),
            {
              name: 'vehicle',
              data: {
                ...(vehicleExposure?.data || {}),
                veh_vin,
              },
              id: key,
            },
          ];
        }

        const submissionAiVehicle = info_ai?.find((ai) =>
          aiMatchesSubject(ai, info_ai_subject as string),
        );

        if (isLeasedOrFinanced) {
          // creates formikAiVehicle based on data from formik
          const formikAiVehicle: Partial<ExposureOrGroup> = {
            name: 'info_ai',
            data: {
              info_ai_name,
              info_ai_subject: [info_ai_subject], // Backend expects array for vehicle AI
              info_ai_type,
              info_ai_address_phone,
            },
          };

          // based on is_added variables added entity to update or add array in updateObject
          if (is_added) {
            delete submissionAiVehicle?.perils;
            delete submissionAiVehicle?.data.socotra_locator;

            formikAiVehicle.id = submissionAiVehicle?.id;

            if (!isEqual(submissionAiVehicle?.data, formikAiVehicle.data)) {
              updateObject.groupFieldsUpdate = [
                ...(updateObject?.groupFieldsUpdate || []),
                formikAiVehicle,
              ];
            }
          } else {
            updateObject.groupFieldsAdd = [
              ...(updateObject?.groupFieldsAdd || []),
              formikAiVehicle,
            ];
          }
        } else if (!isLeasedOrFinanced && submissionAiVehicle) {
          updateObject.groupFieldsDelete = [
            ...(updateObject?.groupFieldsDelete || []),
            omit(submissionAiVehicle, 'data'),
          ];
        }
      }
    });

    setFullscreenLoading(true, 'Saving data');

    // #region send data async requests, sending depends on was it added in updateObject or not
    const hasTaxNumber = !isEmpty(updateObject?.taxNumber?.value);
    const hasUnemploymentNumber =
      !isEmpty(updateObject?.[UNEMPLOYMENT_NUMBER_UW_QUESTION.fieldCode]?.value) &&
      !isEmpty(updateObject?.[UNEMPLOYMENT_NUMBER_UW_QUESTION.questionCode]?.value);

    if (hasTaxNumber || hasUnemploymentNumber) {
      const uwQuestionsPayload = {
        ...submission?.data?.uw_questions,
      };

      if (hasTaxNumber) {
        const { code, value } = updateObject.taxNumber;
        const taxIDType = TAX_ID_TYPE_MAPPED_VALUES[code];

        uwQuestionsPayload.taxIDType = taxIDType;
        uwQuestionsPayload[code] = value;
      }

      if (hasUnemploymentNumber) {
        const fieldCodeValue = updateObject[UNEMPLOYMENT_NUMBER_UW_QUESTION.fieldCode]?.value;
        const questionCodeValue = updateObject[UNEMPLOYMENT_NUMBER_UW_QUESTION.questionCode]?.value;

        uwQuestionsPayload[UNEMPLOYMENT_NUMBER_UW_QUESTION.fieldCode] = fieldCodeValue;
        uwQuestionsPayload[UNEMPLOYMENT_NUMBER_UW_QUESTION.questionCode] = questionCodeValue;
      }

      await updateUwQuestions({
        id: submissionId,
        body: uwQuestionsPayload,
      });
    }

    if (!isEmpty(updateObject.exposuresVehicles)) {
      await bulkUpdateExposure({
        submissionId,
        body: updateObject.exposuresVehicles,
      });
    }

    if (!isEmpty(updateObject.groupFieldsAdd)) {
      await bulkAddGroupField({
        submissionId,
        body: updateObject.groupFieldsAdd,
      });
    }

    if (!isEmpty(updateObject.groupFieldsUpdate)) {
      await bulkUpdateGroupField({
        submissionId,
        body: updateObject.groupFieldsUpdate,
      });
    }

    if (!isEmpty(updateObject.groupFieldsDelete)) {
      await bulkDeleteGroupField({
        submissionId,
        body: updateObject.groupFieldsDelete,
      });
    }
    // #endregion
  };

  /**
   * Handles the payment process when the user clicks the "Pay Now" button.
   *
   * This asynchronous function first validates the form using `validateFormik`.
   * If the form is valid, it proceeds with saving data, updating the quote,
   * and creating a payment plan while managing loading states.
   */
  const handlePayNow = async () => {
    if (isViewOnly) {
      if (isShouldShowFilloutForm) {
        setShouldShowFilloutFormAfterSave(true);
        return;
      }

      navigate(`/submission/${submissionId}/payment`);

      return;
    }

    let isAnyManualAddressInputHasErrors = false;

    const manualAddressInputValidationPromises: Promise<boolean>[] = [];

    for (const ref of Object.values(manualAddressInputRefs.current)) {
      if (ref) {
        manualAddressInputValidationPromises.push(ref.validate());
      }
    }

    const manualAddressInputValidationResults = await Promise.all(
      manualAddressInputValidationPromises,
    );

    if (manualAddressInputValidationResults.some((isValid) => !isValid)) {
      isAnyManualAddressInputHasErrors = true;
    }

    const isValid = await validateFormik();

    if (isValid && !isAnyManualAddressInputHasErrors) {
      try {
        await saveData();

        setFullscreenLoading(true, 'Creating payment');

        await _updatePaymentPlan({
          submissionId,
          body: {
            payment_schedule: submission?.data.paymentScheduleName as TPaymentOption,
          },
        });

        // Update submission status to Payment Required first and redirect to FilloutForm if isShouldShowFilloutForm. Users are going to be redirected to the payment page after filling out the form.
        if (isShouldShowFilloutForm) {
          setTimeout(() => {
            navigate(`?section=2&step=0&show_fillout_form=true`, { replace: true });
          }, SET_TIMEOUT_DELAY_FOR_NAVIGATION);
        } else {
          navigate(`/submission/${submissionId}/payment`);
        }
      } catch (err) {
        displayBackendErrorMessage(err);
        await refetchSubmission();
        window?.scrollTo(0, 0);
      } finally {
        setFullscreenLoading(false);
      }
    }
  };

  /**
   * Handles the click event for the "Previous" button.
   *
   * This asynchronous function first sets a loading state while saving the form data.
   * It attempts to save the data using the `saveData` function, and if successful,
   * it changes the section view to the previous one.
   */
  const handleOnClickPreviousButton = async () => {
    try {
      if (!isViewOnly) {
        await saveData();

        refetchSubmission();
      }

      setSection(1);
    } catch (err) {
      displayBackendErrorMessage(err);
    } finally {
      setFullscreenLoading(false);
    }
  };
  // #endregion

  // Set height of PurchaseNavigationButtons when it will be mounted
  useEffect(() => {
    if (PurchaseNavigationButtonRef.current) {
      const { height } = PurchaseNavigationButtonRef.current.getBoundingClientRect();
      setPurchaseNavigationButtonHeight(height);
    }
  }, []);

  // Check URL params to show FilloutForm directly after save used only once after first save
  useLayoutEffect(() => {
    if (isShouldShowFilloutForm) {
      const searchParamsObject = Object.fromEntries(searchParams.entries());

      if (searchParamsObject.show_fillout_form === 'true') {
        setShouldShowFilloutFormAfterSave(true);
      }
    }
  }, []);

  return shouldShowFilloutFormAfterSave ? (
    <FilloutForm
      ref={PurchaseNavigationButtonRef}
      purchaseNavigationButtonHeight={purchaseNavigationButtonHeight}
    />
  ) : (
    <Stack flex={1} gap={3}>
      <Stack>
        <Typography variant="h1">Finalize your purchase</Typography>
      </Stack>

      {isSubmissionLoading ? (
        <Skeleton width="100%" height="400px" />
      ) : (
        <Stack
          gap={3}
          sx={
            isMobileLayout
              ? {
                  pb: `${purchaseNavigationButtonHeight ? purchaseNavigationButtonHeight - 24 : 0}px`,
                }
              : {}
          }
        >
          <Stack gap={3}>
            {renderOperationsSection && (
              <>
                <FormControl
                  error={formik.touched.hasFein && Boolean(formik.errors.hasFein)}
                  errorMessage={formik.errors.hasFein as string}
                >
                  <Typography variant="h2" mb={1.4}>
                    Do you have a Federal Tax ID Number?
                  </Typography>

                  <Typography variant="body1" mb={1.4}>
                    Also known as a FEIN, this nine-digit number is assigned to businesses by the
                    IRS.
                  </Typography>

                  {!isFeinRequiredForBusinessStructure && (
                    <ToggleButtonsGroup
                      options={DEFAULT_TOGGLE_BUTTONS_GROUP_OPTIONS}
                      onChange={handleOnChangeToggleButtonGroups}
                      value={formik.values.hasFein}
                    />
                  )}
                </FormControl>

                {renderOperationInputField(operationsInputField)}
              </>
            )}

            {renderUnemploymentNumberQuestion && (
              <>
                <FormControl
                  error={
                    formik.touched[UNEMPLOYMENT_NUMBER_UW_QUESTION.questionCode] &&
                    Boolean(formik.errors[UNEMPLOYMENT_NUMBER_UW_QUESTION.questionCode])
                  }
                  errorMessage={
                    formik.errors[UNEMPLOYMENT_NUMBER_UW_QUESTION.questionCode] as string
                  }
                >
                  <Typography variant="h2" mb={1.4}>
                    {UNEMPLOYMENT_NUMBER_UW_QUESTION.questionLabel}
                  </Typography>

                  <ToggleButtonsGroup
                    options={DEFAULT_TOGGLE_BUTTONS_GROUP_OPTIONS}
                    onChange={handleHasUnemploymentNumberChange}
                    value={formik.values[UNEMPLOYMENT_NUMBER_UW_QUESTION.questionCode]}
                  />
                </FormControl>

                {formik.values[UNEMPLOYMENT_NUMBER_UW_QUESTION.questionCode] === 'Yes' && (
                  <FormControl
                    error={Boolean(
                      formik.touched[UNEMPLOYMENT_NUMBER_UW_QUESTION.fieldCode] &&
                      formik.errors[UNEMPLOYMENT_NUMBER_UW_QUESTION.fieldCode],
                    )}
                    errorMessage={
                      formik.errors[UNEMPLOYMENT_NUMBER_UW_QUESTION.fieldCode] as string
                    }
                  >
                    <Typography variant="h2" mb={1.4}>
                      {UNEMPLOYMENT_NUMBER_UW_QUESTION.fieldLabel}
                    </Typography>

                    <Typography variant="body1" mb={1.4}>
                      {UNEMPLOYMENT_NUMBER_UW_QUESTION.fieldHelpText}
                    </Typography>

                    <MuiTextField
                      value={formik.values[UNEMPLOYMENT_NUMBER_UW_QUESTION.fieldCode]}
                      onChange={handleUnemploymentNumberValueChange}
                    />
                  </FormControl>
                )}
              </>
            )}

            {renderPropertiesSection && scheduledBuildings && scheduledBuildings?.length > 0 && (
              <Stack gap={3}>
                <Stack gap={1}>
                  <Typography variant="h2">
                    Do any of these properties have a lienholder(s)?
                  </Typography>

                  <Typography variant="body1">
                    Provide the information for every lienholder on each property. For example if
                    you have a second mortgage on a property include the lienholder for both the 1st
                    and 2nd mortgages.
                  </Typography>
                </Stack>

                {scheduledBuildings?.map((building, _, array) => {
                  const formikBuilding = formik.values[building.id] as PropertiesGroupField;

                  const clearFields = [`${building.id}.info_ai_name`, `${building.id}.address`];

                  return (
                    <Stack key={building.id} gap={1.4}>
                      <Typography variant="h2">{primaryLocationAddressString}</Typography>

                      <FormControl>
                        <FormCheckbox
                          id={`${building.id}.hasLienHolder`}
                          name={`${building.id}.hasLienHolder`}
                          checked={formikBuilding?.hasLienHolder}
                          onChange={(e) => handleCheckboxOnChange(e, clearFields)}
                          labelText="Has lienholder"
                          disabled={isViewOnly}
                        />
                      </FormControl>

                      {formikBuilding.hasLienHolder && (
                        <>
                          <FormControl {...fieldErrorParser(`${building.id}.info_ai_name`)}>
                            <TextField
                              {...fieldErrorParser(`${building.id}.info_ai_name`)}
                              isFieldReadOnly={isViewOnly}
                              field={{
                                code: `${building.id}.info_ai_name`,
                                name: 'Name',
                              }}
                              value={formikBuilding.info_ai_name}
                              onChange={formik.handleChange}
                            />
                          </FormControl>

                          <ManualAddressInput
                            useAutoCompleteAddressInput
                            includedStates={[submission?.us_state as string]}
                            isReadOnly={isViewOnly}
                            ref={(el) => {
                              if (el) {
                                manualAddressInputRefs.current[`${building.id}.address`] = el;
                              } else {
                                delete manualAddressInputRefs.current[`${building.id}.address`];
                              }
                            }}
                            updateAddressField={(address: string) =>
                              formik.setFieldValue(`${building.id}.address`, address)
                            }
                            initialAddress={formikBuilding?.address || ''}
                            useRegularParsingLogicForInitialAddress
                            addressLineMaxLength={ADDRESS_LINE_MAX_LENGTH}
                          />
                        </>
                      )}

                      {array?.length > 1 && <Divider orientation="horizontal" flexItem />}
                    </Stack>
                  );
                })}

                <Stack />
              </Stack>
            )}

            {renderVehiclesSection && (
              <Stack gap={3}>
                <Stack>
                  <Typography variant="h2">
                    Provide the missing VIN numbers and information for every lessor and financier
                    for each vehicle.
                  </Typography>
                </Stack>

                {vehicles?.map((vehicle, _, array) => {
                  const formikVehicle = formik.values[vehicle.id] as VehicleGroupField;

                  const clearFields = [
                    `${vehicle.id}.info_ai_name`,
                    `${vehicle.id}.info_ai_address_phone`,
                  ];

                  return (
                    <Stack key={vehicle.id} gap={1.4}>
                      <Typography variant="h2">{`${vehicle.data.veh_year} ${vehicle.data.veh_make} ${vehicle.data.veh_model}`}</Typography>

                      {isEmpty(vehicle.data.veh_vin) && (
                        <FormControl {...fieldErrorParser(`${vehicle.id}.veh_vin`)}>
                          <TextField
                            {...fieldErrorParser(`${vehicle.id}.veh_vin`)}
                            isFieldReadOnly={isViewOnly}
                            field={{
                              code: `${vehicle.id}.veh_vin`,
                              name: 'VIN Number',
                            }}
                            value={formikVehicle.veh_vin}
                            helperText={
                              shouldApplyShortVinRules(isVehicleVinRequiredEnabled, vehicle.data) &&
                              isShortVin(formikVehicle.veh_vin)
                                ? SHORT_VIN_INFO_MESSAGE
                                : ''
                            }
                            onChange={(event) =>
                              formik.setFieldValue(
                                `${vehicle.id}.veh_vin`,
                                sanitizeVinInput(event.target.value),
                              )
                            }
                            inputProps={{ maxLength: VIN_LENGTH }}
                          />
                        </FormControl>
                      )}

                      <FormControl>
                        <FormCheckbox
                          id={`${vehicle.id}.isLeasedOrFinanced`}
                          name={`${vehicle.id}.isLeasedOrFinanced`}
                          checked={formikVehicle.isLeasedOrFinanced}
                          isFieldOptional
                          onChange={(e) => handleCheckboxOnChange(e, clearFields)}
                          labelText="Leased/financed"
                          disabled={isViewOnly}
                        />
                      </FormControl>

                      {formikVehicle.isLeasedOrFinanced && (
                        <>
                          <FormControl {...fieldErrorParser(`${vehicle.id}.info_ai_name`)}>
                            <TextField
                              {...fieldErrorParser(`${vehicle.id}.info_ai_name`)}
                              field={{
                                code: `${vehicle.id}.info_ai_name`,
                                name: 'Name',
                              }}
                              value={formikVehicle.info_ai_name}
                              onChange={formik.handleChange}
                            />
                          </FormControl>

                          <FormControl {...fieldErrorParser(`${vehicle.id}.info_ai_address_phone`)}>
                            <PatternTextField
                              {...fieldErrorParser(`${vehicle.id}.info_ai_address_phone`)}
                              field={{
                                code: `${vehicle.id}.info_ai_address_phone`,
                                name: 'Phone number',
                              }}
                              value={formikVehicle.info_ai_address_phone}
                              onChange={formik.handleChange}
                              format="(###) ###-####"
                              mask=" "
                              isFieldReadOnly={isViewOnly}
                              inputProps={{ inputMode: 'tel' }}
                            />
                          </FormControl>
                        </>
                      )}

                      {array?.length > 1 && <Divider orientation="horizontal" flexItem />}
                    </Stack>
                  );
                })}
                <Stack />
              </Stack>
            )}
          </Stack>
        </Stack>
      )}

      <Stack
        sx={isMobileLayout ? PurchaseNavigationButtonMobileStyle : {}}
        ref={PurchaseNavigationButtonRef}
      >
        <Tooltip title={isPurchaseButtonDisabled ? purchaseButtonDisabledMessage : ''}>
          <Box>
            <PrimaryButton
              text="Purchase"
              data-test="Purchase"
              onClick={handlePayNow}
              disabled={isPurchaseButtonDisabled}
            />
          </Box>
        </Tooltip>

        <TertiaryButton
          text="Previous"
          onClick={handleOnClickPreviousButton}
          data-test="Previous"
        />
      </Stack>
    </Stack>
  );
};

export default Purchase;
